import { zodResolver } from '@hookform/resolvers/zod';
import { Flex } from '@ironhack/design-system2/components/flex';
import { FormFeedback } from '@ironhack/design-system2/components/form-feedback';
import { omit, pick } from 'ramda';
import { type ReactElement, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useCookie, useSessionStorage } from 'react-use';

import { ContactDetailsFields } from '@/app/components/client/contact-details-fields';
import { FormSubmit } from '@/app/components/server/form-submit';
import { usePageContext } from '@/contexts/page-context/use-page-context';
import { useFormRequest } from '@/hooks/use-form-request';
import { useGtmLocalStorage } from '@/hooks/use-gtm-local-storage';
import { sendEvent, syllabusFormTags } from '@/lib/gtm';
import { TurnstileFormToken } from '@/lib/turnstile/components';
import { regionToCampusMap } from '@/lib/utils';
import { getSyllabusSchema } from '@/lib/utils/form-validations/forms/syllabus-form';
import { type EmailInput } from '@/lib/utils/form-validations/schemas/email-schema';
import { type NameInputs } from '@/lib/utils/form-validations/schemas/name-schema';
import {
  type PhoneInputs,
  getPhoneDefaults,
} from '@/lib/utils/form-validations/schemas/phone-schema';
import { urlToDatoLocale } from '@/lib/utils/locale';
import { type AvailableCourseTrack } from '@/types';
import { type Form as SyllabusFormType } from '../../modules/1-form';

type FormData = {
  cToken: string;
  newsletter: boolean;
  phone: PhoneInputs | undefined;
  referral: string;
} & EmailInput &
  NameInputs;

type Props = {
  onSuccess: () => void;
  syllabusForm: SyllabusFormType;
  course: AvailableCourseTrack;
};

export const Form = (props: Props): ReactElement => {
  const { syllabusForm, onSuccess, course } = props;
  const {
    newsletterLabel,
    buttonText,
    legalText,
    errorMessage,
    withPhone = false,
  } = syllabusForm;
  const {
    params: { language, region },
    pageData: { gaCategory },
  } = usePageContext();
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);

  const formSchema = getSyllabusSchema(syllabusForm);

  const formMethods = useForm<FormData>({
    defaultValues: {
      newsletter: false,
      phone: withPhone
        ? getPhoneDefaults(region)
        : // eslint-disable-next-line no-undefined
          undefined,
      referral: '',
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: zodResolver(formSchema),
  });
  const {
    formState: { isSubmitting },
    handleSubmit,
    setValue,
  } = formMethods;
  const [formFeedback, setFormFeedback] = useState<{
    status: 'error';
    message: string;
  } | null>();

  const formRequest = useFormRequest();
  const [campus] = useSessionStorage('campus', regionToCampusMap[region][0]);
  const [countryName] = useCookie('ss_country');
  const [, setGtmLocalStorage] = useGtmLocalStorage();
  const setCaptchaToken = (token: string) => setValue('cToken', token);

  const onSubmit = async (formData: FormData): Promise<void> => {
    setFormFeedback(null);
    try {
      await formRequest({
        data: {
          ...omit(['newsletter'], formData),
          language,
          bootcamp: `${course.toUpperCase()}FT`,
          campus,
          emailOptIn: formData.newsletter,
        },
        formType: 'syllabus',
      });

      const labels = { region, campus };
      const formReferrer = sessionStorage.getItem('formReferrer') || 'default';
      const { eventCategory = gaCategory, labelKey } =
        syllabusFormTags[formReferrer];

      sendEvent({
        email: formData.email,
        eventAction: 'send syllabus request',
        eventCategory,
        eventLabel: `${labels[labelKey]}::${course}`,
        pageType: eventCategory,
      });
      setGtmLocalStorage({
        applicationData: {
          campus,
          course,
          ...pick(['email', 'firstName', 'lastName'], formData),
        },
        countryName:
          countryName && countryName !== 'undefined'
            ? countryName
            : urlToDatoLocale[`${region}${language}`].slice(-2),
      });
      onSuccess();
    } catch {
      setFormFeedback({ status: 'error', message: errorMessage });
    }
  };

  return (
    <Flex direction="column">
      <FormProvider {...formMethods}>
        <form id="syllabusForm" noValidate onSubmit={handleSubmit(onSubmit)}>
          <Flex alignItems="center" direction="column">
            <ContactDetailsFields
              fields={pick(
                [
                  'chooseBetween',
                  'completeFields',
                  'emailLabel',
                  'emailPlaceholder',
                  'firstNameLabel',
                  'firstNamePlaceholder',
                  'lastNameLabel',
                  'lastNamePlaceholder',
                  'phoneLabel',
                  'phonePlaceholder',
                ],
                syllabusForm
              )}
              mt={4}
              onGoogleClick={() => {
                setFormFeedback(null);
                setIsGoogleSubmitting(true);
              }}
              onGoogleError={() => {
                setIsGoogleSubmitting(false);
                setFormFeedback({
                  status: 'error',
                  message: errorMessage,
                });
              }}
              onGoogleSuccess={() => {
                setIsGoogleSubmitting(false);
                void handleSubmit(onSubmit)();
              }}
              withPhone={withPhone}
            />

            <TurnstileFormToken
              language={language}
              onVerify={setCaptchaToken}
            />
            <FormSubmit
              buttonText={buttonText}
              isLoading={isSubmitting || isGoogleSubmitting}
              legalText={legalText}
              newsletterLabel={newsletterLabel}
              withNewsletter
            />
            {formFeedback?.status && <FormFeedback mt={2} {...formFeedback} />}
          </Flex>
        </form>
      </FormProvider>
    </Flex>
  );
};
